import React, { useCallback, useEffect, useState } from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  useComboboxInputValue,
  useSetComboboxInputValue,
} from './Combobox';

const DEBOUNCE_DELAY = 300;

interface SearchComboboxProps<T> {
  value: T | null;
  onValueChange: (value: T | null) => void;
  fetchItems: (searchTerm: string) => Promise<T[]>;
  renderItem: (item: T) => React.ReactNode;
  renderSelected: (item: T) => React.ReactNode;
  getSearchValue: (item: T) => string;
  getKey: (item: T) => string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  minSearchLength?: number;
  disabled?: boolean;
  className?: string;
}

type SearchComboboxContentProps<T> = Pick<
  SearchComboboxProps<T>,
  'fetchItems' | 'renderItem' | 'getSearchValue' | 'getKey' | 'onValueChange'
> &
  Required<
    Pick<
      SearchComboboxProps<T>,
      | 'searchPlaceholder'
      | 'emptyMessage'
      | 'loadingMessage'
      | 'minSearchLength'
    >
  >;

const SearchComboboxContent = <T,>({
  fetchItems,
  renderItem,
  getSearchValue,
  getKey,
  searchPlaceholder,
  emptyMessage,
  loadingMessage,
  minSearchLength,
  onValueChange,
}: SearchComboboxContentProps<T>) => {
  const searchTerm = useComboboxInputValue();
  const setSearchTerm = useSetComboboxInputValue();
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (searchTerm.length < minSearchLength) {
      setItems([]);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await fetchItems(searchTerm);
        setItems(results);
      } catch {
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timer);
      setIsLoading(false);
    };
  }, [searchTerm, fetchItems, minSearchLength]);

  const handleSelect = useCallback(
    (item: T) => {
      onValueChange(item);
      setSearchTerm('');
      setItems([]);
    },
    [onValueChange, setSearchTerm],
  );

  return (
    <ComboboxDropdown
      searchPlaceholder={searchPlaceholder}
      shouldFilter={false}
    >
      <ComboboxEmpty>
        {isLoading
          ? loadingMessage
          : searchTerm.length < minSearchLength
            ? `Type ${minSearchLength} or more characters to search`
            : emptyMessage}
      </ComboboxEmpty>
      {items.map((item) => (
        <ComboboxOption
          key={getKey(item)}
          onSelect={() => handleSelect(item)}
          searchValue={getSearchValue(item)}
        >
          {renderItem(item)}
        </ComboboxOption>
      ))}
    </ComboboxDropdown>
  );
};

export const SearchCombobox = <T,>({
  value,
  onValueChange,
  fetchItems,
  renderItem,
  renderSelected,
  getSearchValue,
  getKey,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  loadingMessage = 'Searching...',
  minSearchLength = 1,
  disabled = false,
  className,
}: SearchComboboxProps<T>) => {
  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton className={className}>
        {value ? renderSelected(value) : placeholder}
      </ComboboxSelectButton>
      <SearchComboboxContent
        fetchItems={fetchItems}
        renderItem={renderItem}
        getSearchValue={getSearchValue}
        getKey={getKey}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        loadingMessage={loadingMessage}
        minSearchLength={minSearchLength}
        onValueChange={onValueChange}
      />
    </Combobox>
  );
};
